/**
 * Pure helpers that turn an OpenAPI 3 document into documentation data. Everything shown on the Open API
 * documentation page is derived from the live spec through these functions, so the docs cannot drift
 * from the API: no endpoint, scope or schema is listed anywhere else in the frontend.
 */

const METHODS = ['get', 'post', 'put', 'patch', 'delete'];
const MAX_DEPTH = 5;

/** Resolve a local `#/...` reference. Returns undefined for anything that is not resolvable. */
export function resolveRef(spec, ref) {
    if (typeof ref !== 'string' || !ref.startsWith('#/')) return undefined;
    return ref.slice(2).split('/').reduce(
        (acc, part) => (acc == null ? undefined : acc[part.replace(/~1/g, '/').replace(/~0/g, '~')]),
        spec,
    );
}

/** Follow `$ref` chains to the concrete node (cycle-safe). */
export function deref(spec, node) {
    let cur = node;
    const seen = new Set();
    while (cur && typeof cur === 'object' && cur.$ref) {
        if (seen.has(cur.$ref)) return {};
        seen.add(cur.$ref);
        cur = resolveRef(spec, cur.$ref);
    }
    return cur ?? {};
}

export function typeLabel(spec, schema) {
    const s = deref(spec, schema);
    if (s.enum) return `enum(${s.enum.join(' | ')})`;
    if (s.type === 'array') return `array<${typeLabel(spec, s.items || {})}>`;
    if (s.allOf || s.oneOf || s.anyOf) return (s.allOf ? 'allOf' : s.oneOf ? 'oneOf' : 'anyOf');
    const base = s.type || (s.properties ? 'object' : 'any');
    return s.format ? `${base} (${s.format})` : base;
}

/** Merge `allOf` members into one object schema (properties + required). */
function flattenAllOf(spec, schema) {
    const s = deref(spec, schema);
    if (!s.allOf) return s;
    const merged = { ...s, properties: { ...(s.properties || {}) }, required: [...(s.required || [])] };
    for (const part of s.allOf) {
        const p = flattenAllOf(spec, part);
        Object.assign(merged.properties, p.properties || {});
        merged.required.push(...(p.required || []));
        if (!merged.type && p.type) merged.type = p.type;
    }
    delete merged.allOf;
    return merged;
}

/**
 * Flatten a schema into table rows `{ name, type, required, description }`. Nested objects and arrays of objects
 * are expanded with dotted names (`items[].id`). `$ref`s are resolved; a ref already on the path is not re-entered.
 */
export function schemaRows(spec, schema, prefix = '', required = false, trail = [], depth = 0) {
    if (!schema) return [];
    const refKey = schema.$ref;
    if (refKey && trail.includes(refKey)) {
        // Recursive type: list the field, do not expand it again.
        return prefix ? [{ name: prefix, type: typeLabel(spec, schema), required, description: deref(spec, schema).description || '' }] : [];
    }
    const nextTrail = refKey ? [...trail, refKey] : trail;
    const s = flattenAllOf(spec, schema);
    const rows = [];

    if (prefix) rows.push({ name: prefix, type: typeLabel(spec, s), required, description: s.description || '' });

    if (depth >= MAX_DEPTH) return rows;
    if (s.type === 'array' && s.items) {
        const item = flattenAllOf(spec, s.items);
        if (item.properties || item.allOf || item.type === 'array') {
            rows.push(...schemaRows(spec, s.items, `${prefix}[]`, false, nextTrail, depth + 1).filter((r) => r.name !== `${prefix}[]`));
        }
        return rows;
    }
    if (s.properties) {
        const req = new Set(s.required || []);
        for (const [key, child] of Object.entries(s.properties)) {
            const name = prefix ? `${prefix}.${key}` : key;
            rows.push(...schemaRows(spec, child, name, req.has(key), nextTrail, depth + 1));
        }
    }
    return rows;
}

/** Scopes a security requirement list demands, e.g. `[{ ApiKey: ['projects:read'] }]` -> `['projects:read']`. */
export function scopesOf(op) {
    const out = new Set();
    for (const req of op.security || []) for (const list of Object.values(req || {})) for (const sc of list || []) out.add(sc);
    for (const sc of [].concat(op['x-required-scope'] || op['x-scopes'] || [])) out.add(sc);
    return [...out];
}

/** Every operation in the spec, with parameters/body/responses resolved. */
export function listOperations(spec) {
    const ops = [];
    for (const [path, item] of Object.entries(spec?.paths || {})) {
        const shared = item.parameters || [];
        for (const method of METHODS) {
            const op = item[method];
            if (!op) continue;
            const params = [...shared, ...(op.parameters || [])].map((p) => deref(spec, p));
            const bodyContent = deref(spec, op.requestBody).content || {};
            const bodySchema = (bodyContent['application/json'] || Object.values(bodyContent)[0] || {}).schema;
            const responses = Object.entries(op.responses || {}).map(([status, r]) => {
                const resp = deref(spec, r);
                const content = resp.content || {};
                const schema = (content['application/json'] || Object.values(content)[0] || {}).schema;
                return { status, description: resp.description || '', schema, headers: Object.keys(resp.headers || {}) };
            });
            ops.push({
                method: method.toUpperCase(),
                path,
                tag: (op.tags && op.tags[0]) || 'Other',
                summary: op.summary || '',
                description: op.description || '',
                operationId: op.operationId || '',
                scopes: scopesOf(op),
                parameters: params,
                bodySchema,
                responses,
            });
        }
    }
    return ops;
}

export function groupByTag(ops) {
    const groups = new Map();
    for (const op of ops) {
        if (!groups.has(op.tag)) groups.set(op.tag, []);
        groups.get(op.tag).push(op);
    }
    return [...groups.entries()].map(([tag, list]) => ({ tag, ops: list }));
}

export function filterOperations(ops, query) {
    const q = (query || '').trim().toLowerCase();
    if (!q) return ops;
    return ops.filter((o) => [o.method, o.path, o.tag, o.summary, o.operationId, ...o.scopes].join(' ').toLowerCase().includes(q));
}

/** scope -> number of operations that require it, from the spec alone. */
export function scopeUsage(ops) {
    const map = new Map();
    for (const op of ops) for (const sc of op.scopes) map.set(sc, [...(map.get(sc) || []), `${op.method} ${op.path}`]);
    return [...map.entries()].map(([scope, uses]) => ({ scope, uses })).sort((a, b) => a.scope.localeCompare(b.scope));
}

/** Pagination-style query parameters actually declared by the spec (name -> description). */
export function paginationParams(ops) {
    const names = ['limit', 'page', 'pagesize', 'page_size', 'offset', 'cursor', 'starting_after', 'after'];
    const found = new Map();
    for (const op of ops) {
        for (const p of op.parameters) {
            if (p.in === 'query' && names.includes(String(p.name).toLowerCase()) && !found.has(p.name)) found.set(p.name, p.description || '');
        }
    }
    return [...found.entries()].map(([name, description]) => ({ name, description }));
}

/** A representative value for a schema, preferring spec-provided example/default/enum. */
export function sampleValue(spec, schema, depth = 0) {
    const s = flattenAllOf(spec, schema);
    if (s.example !== undefined) return s.example;
    if (s.default !== undefined) return s.default;
    if (s.enum) return s.enum[0];
    if (s.type === 'array') return depth > 3 ? [] : [sampleValue(spec, s.items || {}, depth + 1)];
    if (s.type === 'object' || s.properties) {
        const out = {};
        if (depth > 3) return out;
        const req = new Set(s.required || []);
        for (const [k, child] of Object.entries(s.properties || {})) if (req.has(k)) out[k] = sampleValue(spec, child, depth + 1);
        return out;
    }
    if (s.type === 'integer' || s.type === 'number') return 0;
    if (s.type === 'boolean') return false;
    return 'string';
}

export const KEY_PLACEHOLDER = 'plk_live_YOUR_KEY';

/** Copyable curl for one operation: base URL + path, required query params, placeholder key, JSON body. */
export function buildCurl(spec, baseUrl, op) {
    let path = op.path.replace(/\{([^}]+)\}/g, '<$1>');
    const query = op.parameters.filter((p) => p.in === 'query' && p.required).map((p) => `${p.name}=<${p.name}>`);
    if (query.length) path += `?${query.join('&')}`;
    const lines = [`curl -X ${op.method} "${baseUrl}${path}"`, `  -H "Authorization: Bearer ${KEY_PLACEHOLDER}"`];
    for (const p of op.parameters.filter((x) => x.in === 'header' && x.required)) {
        lines.push(`  -H "${p.name}: <${p.name === 'Idempotency-Key' ? 'unique-id' : p.name}>"`);
    }
    if (op.bodySchema) {
        lines.push('  -H "Content-Type: application/json"');
        lines.push(`  -d '${JSON.stringify(sampleValue(spec, op.bodySchema))}'`);
    }
    return lines.join(' \\\n');
}
